// Client-side "Add to Calendar" helpers — no backend/auth required.
// Sessions are assumed to be in a single (UK) timezone, so we use floating
// local time in both the .ics file and the Google Calendar link.

const pad2 = (n) => String(n).padStart(2, '0');

function parseDurationMinutes(duration) {
  if (!duration) return 60;
  const str = String(duration).toLowerCase();
  let total = 0;
  const hrMatch = str.match(/(\d+(?:\.\d+)?)\s*(?:h|hr|hrs|hour|hours)/);
  if (hrMatch) total += parseFloat(hrMatch[1]) * 60;
  const minMatch = str.match(/(\d+(?:\.\d+)?)\s*(?:m|min|mins|minute|minutes)/);
  if (minMatch) total += parseFloat(minMatch[1]);
  if (!hrMatch && !minMatch) {
    const bare = str.match(/(\d+(?:\.\d+)?)/);
    if (bare) total = parseFloat(bare[1]);
  }
  return total > 0 ? Math.round(total) : 60;
}

function sessionStartEnd(s) {
  if (!s?.dateISO) return null;
  const [y, m, d] = s.dateISO.split('-').map(Number);
  let hh = 9, mm = 0;
  if (s.time && /^\d{1,2}:\d{2}/.test(s.time)) {
    const [h, mi] = s.time.split(':').map(Number);
    hh = h; mm = mi;
  }
  const start = new Date(y, m - 1, d, hh, mm, 0);
  const end = new Date(start.getTime() + parseDurationMinutes(s.duration) * 60000);
  return { start, end };
}

const fmtIcsLocal = (dt) => `${dt.getFullYear()}${pad2(dt.getMonth() + 1)}${pad2(dt.getDate())}T${pad2(dt.getHours())}${pad2(dt.getMinutes())}00`;

const escapeIcsText = (str) => String(str || '').replace(/\\/g, '\\\\').replace(/\n/g, '\\n').replace(/,/g, '\\,').replace(/;/g, '\\;');

export function buildIcsForSessions(sessions) {
  const events = (sessions || []).map((s) => {
    const se = sessionStartEnd(s);
    if (!se) return null;
    const desc = [s.description, s.coach ? `Coach/host: ${s.coach}` : null].filter(Boolean).join('\n');
    return [
      'BEGIN:VEVENT',
      `UID:spc-session-${s.id}@spcclubhouse.co.uk`,
      `DTSTAMP:${fmtIcsLocal(new Date())}Z`,
      `DTSTART:${fmtIcsLocal(se.start)}`,
      `DTEND:${fmtIcsLocal(se.end)}`,
      `SUMMARY:${escapeIcsText(s.title || 'SPC session')}`,
      s.location ? `LOCATION:${escapeIcsText(s.location)}` : null,
      desc ? `DESCRIPTION:${escapeIcsText(desc)}` : null,
      'END:VEVENT',
    ].filter(Boolean).join('\r\n');
  }).filter(Boolean);
  return ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//SPC Clubhouse//Sessions//EN', 'CALSCALE:GREGORIAN', ...events, 'END:VCALENDAR'].join('\r\n');
}

export function downloadIcs(sessions, filename = 'spc-session.ics') {
  const blob = new Blob([buildIcsForSessions(sessions)], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function googleCalendarUrl(s) {
  const se = sessionStartEnd(s);
  if (!se) return null;
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: s.title || 'SPC session',
    dates: `${fmtIcsLocal(se.start)}/${fmtIcsLocal(se.end)}`,
    location: s.location || '',
    details: [s.description, s.coach ? `Coach/host: ${s.coach}` : ''].filter(Boolean).join('\n'),
    ctz: 'Europe/London',
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}
